import type { GradedAttemptTab } from '@/common/api/models/gradedAttemptTab';
import type { Task } from '@/common/api/models/task';
import type { Module } from '@/student/types';
import type { ExamAttemptStatusPayload } from '@/student/grade/examAttemptCopy';
import {
  baseModule,
  buildOralPassTask,
  buildWrittenApprovalTasks,
  dimGrades,
  mcqTask,
  oralExamTask,
  ORAL_QUESTIONS,
} from '@/student/grade/fixtures/gradeReportScenarios';

export type GradeReportApprovalPresetId =
  | 'written-pass'
  | 'written-pass-mcq'
  | 'written-fail-oral'
  | 'written-fail-services'
  | 'written-exhausted'
  | 'oral-pass'
  | 'oral-retry'
  | 'oral-exhausted'
  | 'earlier-written'
  | 'earlier-oral'
  | 'archive-loading'
  | 'archive-error'
  | 'reopen-busy'
  | 'reopen-error';

export type GradeReportArchiveState = 'ready' | 'loading' | 'error';

export interface GradeReportAttemptSnapshot {
  grade: number;
  comment: string;
  tasks: Task[];
  mode: GradedAttemptTab['mode'];
}

export interface GradeReportApprovalPreset {
  id: GradeReportApprovalPresetId;
  chipLabel: string;
  module: Module;
  /** Latest sitting tasks when there is no history, or the current attempt's tasks. */
  tasks: Task[];
  attempts?: GradeReportAttemptSnapshot[];
  initialSelectedIndex?: number;
  archiveState?: GradeReportArchiveState;
  noteBusy?: boolean;
  noteError?: string | null;
}

const WRITTEN_PASS_COMMENT = '<p>Module feedback: consistent quality across transfer and regular tasks on the first sitting.</p>';
const WRITTEN_FAIL_COMMENT = '<p>Module feedback: written exam did not meet the pass threshold; review transfer task evidence.</p>';

const oralFailTask = () => oralExamTask(
  ORAL_QUESTIONS.slice(0, 3).map((question) => ({
    id: question.questionId,
    dimGrades: dimGrades({
      content: 'satisfactory',
      depth: 'needs_improvement',
      application: 'satisfactory',
      communication: 'good',
      professionalism: 'satisfactory',
    }),
    questionFeedback: `<p>Current oral sitting feedback for ${question.questionId}.</p>`,
  })),
  { overallFeedback: '<p>Oral sitting overall: gaps in depth and application remain.</p>' },
);

const earlierOralQuestions = [
  {
    questionId: 'arch-q1',
    question: 'Sampling frame: explain your inclusion criteria from the first oral sitting.',
  },
  {
    questionId: 'arch-q2',
    question: 'Observer bias: how you mitigated bias during the first oral sitting.',
  },
  {
    questionId: 'arch-q3',
    question: 'Analytic method: defend the method you chose on the first oral sitting.',
  },
];

const earlierOralTask = () => oralExamTask(
  earlierOralQuestions.map((question) => ({
    id: question.questionId,
    dimGrades: dimGrades({ content: 'good', depth: 'satisfactory', communication: 'good' }),
    questionFeedback: `<p>Archived feedback unique to ${question.questionId}.</p>`,
  })),
  {
    overallFeedback: '<p>Earlier oral sitting overall assessment.</p>',
    questionPool: earlierOralQuestions,
  },
);

function examAttempt(overrides: ExamAttemptStatusPayload): ExamAttemptStatusPayload {
  return {
    enabled: true,
    used: 1,
    allowed: 3,
    ...overrides,
  };
}

function toGradedTabs(attempts: GradeReportAttemptSnapshot[]): GradedAttemptTab[] {
  return attempts.map((row, index) => ({
    number: index + 1,
    mode: row.mode,
    grade: row.grade,
    countedAt: `2026-0${index + 6}-12T10:00:00.000Z`,
    comment: row.comment,
  }));
}

const writtenAttemptsForHistory: GradeReportAttemptSnapshot[] = [
  {
    grade: 5.0,
    comment: WRITTEN_FAIL_COMMENT,
    tasks: buildWrittenApprovalTasks(),
    mode: 'written',
  },
  {
    grade: 5.0,
    comment: '<p>Oral fail feedback on the current sitting.</p>',
    tasks: [oralFailTask()],
    mode: 'oral',
  },
];

export const GRADE_REPORT_APPROVAL_PRESET_IDS: GradeReportApprovalPresetId[] = [
  'written-pass',
  'written-pass-mcq',
  'written-fail-oral',
  'written-fail-services',
  'written-exhausted',
  'oral-pass',
  'oral-retry',
  'oral-exhausted',
  'earlier-written',
  'earlier-oral',
  'archive-loading',
  'archive-error',
  'reopen-busy',
  'reopen-error',
];

export const DEFAULT_APPROVAL_PRESET_ID: GradeReportApprovalPresetId = 'written-pass';

export function isGradeReportApprovalPresetId(
  value: string | null | undefined,
): value is GradeReportApprovalPresetId {
  return !!value && GRADE_REPORT_APPROVAL_PRESET_IDS.includes(value as GradeReportApprovalPresetId);
}

export function resolveApprovalPresetId(
  search: string,
): GradeReportApprovalPresetId {
  if (typeof window === 'undefined') {
    const params = new URLSearchParams(search);
    const preset = params.get('preset');
    return isGradeReportApprovalPresetId(preset) ? preset : DEFAULT_APPROVAL_PRESET_ID;
  }
  const params = new URLSearchParams(search || window.location.search);
  const preset = params.get('preset');
  return isGradeReportApprovalPresetId(preset) ? preset : DEFAULT_APPROVAL_PRESET_ID;
}

export const GRADE_REPORT_APPROVAL_PRESETS: Record<GradeReportApprovalPresetId, GradeReportApprovalPreset> = {
  'written-pass': {
    id: 'written-pass',
    chipLabel: 'Written pass',
    module: baseModule({
      grading: { grade: 1.7, comment: WRITTEN_PASS_COMMENT },
      examAttempt: examAttempt({ nextAction: 'passed', used: 1 }),
    }),
    tasks: buildWrittenApprovalTasks(),
  },
  'written-pass-mcq': {
    id: 'written-pass-mcq',
    chipLabel: 'Written pass + MCQ',
    module: baseModule({
      grading: { grade: 2.0, comment: '<p>Module feedback: proctored knowledge check included in the grade.</p>' },
      examAttempt: examAttempt({ nextAction: 'passed', used: 1 }),
    }),
    tasks: [...buildWrittenApprovalTasks(), mcqTask()],
  },
  'written-fail-oral': {
    id: 'written-fail-oral',
    chipLabel: 'Written fail → oral',
    module: baseModule({
      grading: { grade: 5.0, comment: WRITTEN_FAIL_COMMENT },
      examAttempt: examAttempt({ nextAction: 'upgrade_to_oral', used: 1 }),
    }),
    tasks: buildWrittenApprovalTasks(),
  },
  'written-fail-services': {
    id: 'written-fail-services',
    chipLabel: 'Written fail → services',
    module: baseModule({
      grading: { grade: 5.0, comment: WRITTEN_FAIL_COMMENT },
      examAttempt: examAttempt({ nextAction: 'needs_student_services', used: 1 }),
    }),
    tasks: buildWrittenApprovalTasks(),
  },
  'written-exhausted': {
    id: 'written-exhausted',
    chipLabel: 'Written exhausted',
    module: baseModule({
      grading: { grade: 5.0, comment: WRITTEN_FAIL_COMMENT },
      examAttempt: examAttempt({ nextAction: 'exhausted', used: 3, allowed: 3 }),
    }),
    tasks: buildWrittenApprovalTasks(),
  },
  'oral-pass': {
    id: 'oral-pass',
    chipLabel: 'Oral pass',
    module: baseModule({
      grading: {
        grade: 2.0,
        comment: '<p>Module feedback: oral examination determines the module grade.</p>',
      },
      examAttempt: examAttempt({ nextAction: 'passed', used: 1 }),
    }),
    tasks: [buildOralPassTask()],
  },
  'oral-retry': {
    id: 'oral-retry',
    chipLabel: 'Oral retry + history',
    module: baseModule({
      grading: { grade: 5.0, comment: '<p>Oral fail feedback on the current sitting.</p>' },
      examAttempt: examAttempt({ nextAction: 'take_next_attempt', used: 2 }),
    }),
    tasks: [oralFailTask()],
    attempts: writtenAttemptsForHistory,
    initialSelectedIndex: 1,
  },
  'oral-exhausted': {
    id: 'oral-exhausted',
    chipLabel: 'Oral exhausted',
    module: baseModule({
      grading: { grade: 5.0, comment: '<p>Oral fail feedback with no attempts remaining.</p>' },
      examAttempt: examAttempt({ nextAction: 'exhausted', used: 3, allowed: 3 }),
    }),
    tasks: [oralFailTask()],
  },
  'earlier-written': {
    id: 'earlier-written',
    chipLabel: 'Earlier written sheet',
    module: baseModule({
      grading: { grade: 5.0, comment: WRITTEN_FAIL_COMMENT },
      examAttempt: examAttempt({ nextAction: 'take_next_attempt', used: 2 }),
    }),
    tasks: buildWrittenApprovalTasks(),
    attempts: writtenAttemptsForHistory,
    initialSelectedIndex: 0,
  },
  'earlier-oral': {
    id: 'earlier-oral',
    chipLabel: 'Earlier oral sheet',
    module: baseModule({
      grading: { grade: 3.0, comment: '<p>Current sitting oral feedback.</p>' },
      examAttempt: examAttempt({ nextAction: 'passed', used: 2 }),
    }),
    tasks: [oralFailTask()],
    attempts: [
      {
        grade: 3.7,
        comment: '<p>Earlier oral sitting module comment.</p>',
        tasks: [earlierOralTask()],
        mode: 'oral',
      },
      {
        grade: 3.0,
        comment: '<p>Current sitting oral feedback.</p>',
        tasks: [oralFailTask()],
        mode: 'oral',
      },
    ],
    initialSelectedIndex: 0,
  },
  'archive-loading': {
    id: 'archive-loading',
    chipLabel: 'Archive loading',
    module: baseModule({
      grading: { grade: 3.0, comment: '<p>Current oral sitting.</p>' },
      examAttempt: examAttempt({ nextAction: 'passed', used: 2 }),
    }),
    tasks: [oralFailTask()],
    attempts: writtenAttemptsForHistory,
    initialSelectedIndex: 0,
    archiveState: 'loading',
  },
  'archive-error': {
    id: 'archive-error',
    chipLabel: 'Archive error',
    module: baseModule({
      grading: { grade: 3.0, comment: '<p>Current oral sitting.</p>' },
      examAttempt: examAttempt({ nextAction: 'passed', used: 2 }),
    }),
    tasks: [oralFailTask()],
    attempts: writtenAttemptsForHistory,
    initialSelectedIndex: 0,
    archiveState: 'error',
  },
  'reopen-busy': {
    id: 'reopen-busy',
    chipLabel: 'Reopen busy',
    module: baseModule({
      grading: { grade: 5.0, comment: WRITTEN_FAIL_COMMENT },
      examAttempt: examAttempt({ nextAction: 'upgrade_to_oral', used: 1 }),
    }),
    tasks: buildWrittenApprovalTasks(),
    noteBusy: true,
  },
  'reopen-error': {
    id: 'reopen-error',
    chipLabel: 'Reopen error',
    module: baseModule({
      grading: { grade: 5.0, comment: WRITTEN_FAIL_COMMENT },
      examAttempt: examAttempt({ nextAction: 'upgrade_to_oral', used: 1 }),
    }),
    tasks: buildWrittenApprovalTasks(),
    noteError: 'That did not work. You are still on this report. Try again.',
  },
};

export function gradedTabsForPreset(preset: GradeReportApprovalPreset): GradedAttemptTab[] {
  if (!preset.attempts || preset.attempts.length < 2) {
    return [];
  }
  return toGradedTabs(preset.attempts);
}

export function snapshotForPreset(
  preset: GradeReportApprovalPreset,
  selectedIndex: number,
): GradeReportAttemptSnapshot {
  if (preset.attempts && preset.attempts.length > 0) {
    const clamped = Math.min(Math.max(selectedIndex, 0), preset.attempts.length - 1);
    return preset.attempts[clamped];
  }
  return {
    grade: preset.module.grading?.grade ?? 0,
    comment: preset.module.grading?.comment ?? '',
    tasks: preset.tasks,
    mode: 'written',
  };
}
